/**
 * CombatPowerBlock 共有のフォーマッタ・Q 解析ユーティリティ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 戦闘力ブロックの整形・会計年度 Q パース補助
 * @graph-connects none
 */

import type { MonthData } from '../../../types/combat-power.js';

/**
 * "{fy}_{q}Q" 形式の Q タイトルをパース。
 * @graph-connects none
 */
function parseQuarter(title: string): { fy: number; q: number } | null {
  const m = title.match(/^(\d{4})_([1-4])Q/);
  if (!m) return null;
  return { fy: parseInt(m[1], 10), q: parseInt(m[2], 10) };
}

/**
 * 最新 Q の翌Qタイトル ("まとめ" なし) を生成。
 * @graph-connects none
 */
export function nextQuarterTitle(existingTitles: string[]): string | null {
  const parsed = existingTitles
    .map(parseQuarter)
    .filter((q): q is { fy: number; q: number } => q !== null);
  if (parsed.length === 0) return null;
  const latest = parsed.reduce((acc, cur) =>
    cur.fy > acc.fy || (cur.fy === acc.fy && cur.q > acc.q) ? cur : acc,
  );
  const next = latest.q < 4 ? { fy: latest.fy, q: latest.q + 1 } : { fy: latest.fy + 1, q: 1 };
  return `${String(next.fy)}_${String(next.q)}Q`;
}

/** @graph-connects none */
export function formatNum(v: number | null, decimals = 2): string {
  if (v === null) return '-';
  const fixed = v.toFixed(decimals);
  return fixed.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
}

/**
 * pt の表示文字列を `完了(処理中)` 形式で組み立てる。処理中が無ければ括弧なしで完了のみ。
 *
 * 戦闘力テーブルの実績Pt 列と要件定義テーブルの pt 列で**同じ字面**を出すための共有実装。
 * 片方だけ `完了+処理中` の 1 値にすると、同じカードに並ぶ 2 つの表で同じ人の pt が
 * 違う形で出てしまう。
 *
 * @graph-business pt を `完了(処理中)` 形式の表示文字列へ整形する (戦闘力テーブル / 要件定義テーブル共有)
 * @graph-connects none
 */
export function formatPtWithWip(completed: number, inProgress: number): string {
  return inProgress > 0
    ? `${formatNum(completed, 1)}(${formatNum(inProgress, 1)})`
    : formatNum(completed, 1);
}

/**
 * 実績戦闘力の表示文字列を組み立てる。
 * backlog 実績がある月は `完了戦闘力(処理中含め戦闘力)` 形式 (処理中が無ければ括弧なし)、
 * backlog が無い月はシート由来の actualPower をそのまま表示する。
 * 月別ビュー (TeamTable) と四半期ビュー (QuarterTeamTable) で共有する。
 * @graph-business 実績戦闘力の `完了(処理中含め)` 表示フォーマッタ (月別/四半期ビュー共有)
 * @graph-connects none
 */
export function formatActualPowerDisplay(cm: MonthData): string {
  const bp = cm.backlogPt;
  const completedPower = bp && cm.businessDays > 0 ? bp.completed / cm.businessDays : null;
  if (completedPower === null) return formatNum(cm.actualPower, 1);
  const totalPower = bp ? (bp.completed + bp.inProgress) / cm.businessDays : null;
  return bp && bp.inProgress > 0 && totalPower !== null
    ? `${formatNum(completedPower, 1)}(${formatNum(totalPower, 1)})`
    : formatNum(completedPower, 1);
}

/**
 * 月ヘッダ配列（例 ["2026-4月", "2026-5月", "2026-6月"]）から「当月」の列インデックスを求める。
 *
 * 当月が含まれていない Q（過去 Q / 未来 Q）を開いているときは、未来に踏み込まない最新月へ寄せる。
 * それも無ければ先頭列を返す。エンジニア看板の既定月と PJ Sandbox の期待戦闘力参照が
 * 同じ月を指すよう、口径をここに 1 本化する。
 * @graph-connects none
 */
export function detectCurrentMonthIdx(monthHeaders: readonly string[]): number {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const parsed = monthHeaders.map((header) => {
    const m = /^(\d{4})-(\d{1,2})月$/.exec(header);
    return m === null ? null : { year: Number(m[1]), month: Number(m[2]) };
  });

  const exact = parsed.findIndex((p) => p?.year === currentYear && p.month === currentMonth);
  if (exact !== -1) return exact;

  for (let i = parsed.length - 1; i >= 0; i--) {
    const p = parsed[i];
    if (p === null || p === undefined) continue;
    if (p.year < currentYear || (p.year === currentYear && p.month <= currentMonth)) return i;
  }
  return 0;
}

/**
 * "2026-4月" → "2026-04"
 * @graph-connects none
 */
export function monthHeaderToYyyymm(header: string): string | null {
  const m = header.match(/^(\d{4})-(\d{1,2})月$/);
  if (!m) return null;
  return `${m[1]}-${m[2].padStart(2, '0')}`;
}

/** ドロップダウン先頭に挿入する仮想 option の値。
 * @graph-connects none
 */
export const ADD_NEW_QUARTER_VALUE = '__add_new_quarter__';
